import type { NativeCheck, Severity } from '@fraudeai/schema';
import { defineRule, type Rule } from '../types.js';

/**
 * Целостность встроенных функций и следы инструментов приватности.
 *
 * Отдельная группа, потому что вывод здесь другого рода: мы не сравниваем два
 * слоя между собой, а проверяем, не переписаны ли сами измерительные приборы.
 * Если `getImageData` подменён, любые выводы по canvas ничего не стоят —
 * и об этом честнее сказать прямо, а не молча выдать «чисто».
 */

/** Человеческие названия для путей к функциям. */
const PATH_LABELS: [RegExp, string][] = [
  [/^Navigator\.prototype\./, 'данные о браузере'],
  [/^Screen\.prototype\./, 'параметры экрана'],
  [/Canvas/, 'отрисовка canvas'],
  [/WebGL/, 'графический контекст WebGL'],
  [/Audio/, 'обработка звука'],
  [/^Date\.|^Intl\./, 'время и локаль'],
  [/getBoundingClientRect|getClientRects/, 'геометрия элементов'],
  [/MediaDevices|SpeechSynthesis/, 'устройства и голоса'],
  [/^Function\.|^Object\.|^Reflect\./, 'базовые функции языка'],
];

function labelFor(path: string): string {
  for (const [pattern, label] of PATH_LABELS) {
    if (pattern.test(path)) return label;
  }
  return 'встроенные функции';
}

function reasonsFor(check: NativeCheck): string[] {
  const reasons: string[] = [];
  if (check.toStringTampered) reasons.push('исходник не выглядит нативным');
  if (check.unexpectedPrototype) reasons.push('это обычная функция JavaScript, а не встроенная');
  if (check.foreignReceiverAccepted) reasons.push('геттер не отвергает чужой объект');
  if (check.descriptorTampered) reasons.push('изменён тип свойства');
  if (check.ownPropertyShadow) reasons.push('свойство переопределено поверх прототипа');
  if (check.realmMismatch) reasons.push('отличается от той же функции в чистом окружении');
  return reasons;
}

export const integrityRules: Rule[] = [
  defineRule({
    meta: {
      id: 'int.natives-tampered',
      title: 'Встроенные функции браузера подменены',
      severity: 'critical',
      category: 'spoofing',
      flags: ['ANTIDETECT', 'SPOOFING'],
      weight: 40,
      evidenceTier: 'direct',
      evidenceGroup: 'native-shape',
      requires: ['natives'],
    },
    evaluate(signals) {
      const natives = signals.natives;
      if (!natives || natives.tamperedCount === 0) return null;

      const tampered = natives.checks.filter((c) => reasonsFor(c).length > 0);
      const areas = [...new Set(tampered.map((c) => labelFor(c.path)))];

      if (signals.navigator?.parsed?.engine === 'Gecko' && !natives.toStringItselfTampered) {
        return {
          severity: 'low',
          confidence: 0.45,
          flags: ['PRIVACY_TOOLING'],
          explanation:
            'В Firefox такие отличия встроенных getter’ов часто появляются из-за ' +
            'антифингерпринтинга или расширений приватности. Это полезно показать ' +
            'в отчёте, но без подмены Function.prototype.toString и без других ' +
            'независимых улик не стоит называть обычный Firefox антидетектом.',
          evidence: tampered.slice(0, 8).map((c) => ({
            label: c.path,
            detail: reasonsFor(c).join('; '),
          })),
        };
      }

      // Одна подменённая функция может быть побочным эффектом расширения;
      // несколько сразу — уже целенаправленная маскировка.
      const severity: Severity = tampered.length >= 3 ? 'critical' : 'high';
      const confidence = Math.min(0.5 + tampered.length * 0.15, 1);

      return {
        severity,
        confidence,
        explanation:
          `Переопределено встроенных функций: ${tampered.length}. Затронуты ${areas.join(', ')}. ` +
          'Браузер не меняет свои функции сам — это делает расширение или ' +
          'антидетект-сборка, чтобы выдавать поддельные значения.',
        evidence: tampered.slice(0, 8).map((c) => ({
          label: c.path,
          detail: reasonsFor(c).join('; '),
        })),
      };
    },
  }),

  defineRule({
    meta: {
      id: 'int.tostring-tampered',
      title: 'Подменён механизм проверки нативности функций',
      severity: 'critical',
      category: 'spoofing',
      flags: ['ANTIDETECT', 'SPOOFING'],
      weight: 45,
      evidenceTier: 'direct',
      evidenceGroup: 'native-shape',
      requires: ['natives'],
    },
    evaluate(signals) {
      if (!signals.natives?.toStringItselfTampered) return null;
      return {
        explanation:
          'Переопределён сам Function.prototype.toString — функция, которой проверяют, ' +
          'является ли код встроенным. Её меняют ровно с одной целью: скрыть, что ' +
          'подменены другие функции. Это верхний уровень маскировки, и сам по себе ' +
          'он куда красноречивее того, что за ним прячут.',
        evidence: [
          { label: 'Function.prototype.toString', expected: 'встроенная функция', actual: 'переопределена' },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'int.accessor-replaced',
      title: 'Свойства браузера читаются подставными функциями',
      severity: 'critical',
      category: 'spoofing',
      flags: ['ANTIDETECT', 'SPOOFING'],
      weight: 40,
      evidenceTier: 'direct',
      evidenceGroup: 'native-call-semantics',
      requires: ['natives'],
    },
    evaluate(signals) {
      const natives = signals.natives;
      if (!natives) return null;
      if (!natives.proxyOnNavigator && !natives.proxyOnScreen) return null;

      const targets = natives.checks
        .filter((c) => c.foreignReceiverAccepted)
        .map((c) => c.path);

      return {
        explanation:
          'Встроенный геттер обязан отказаться работать, если его позвать с посторонним ' +
          'объектом. Здесь он спокойно возвращает значение — значит, на его месте ' +
          'обычная функция JavaScript, подставленная вместо оригинала.',
        evidence: targets.slice(0, 8).map((path) => ({
          label: path,
          expected: 'ошибка при чужом объекте',
          actual: 'значение возвращено',
        })),
      };
    },
  }),

  defineRule({
    meta: {
      id: 'int.no-pristine-realm',
      title: 'Не удалось получить эталонное окружение для сверки',
      severity: 'info',
      category: 'inconsistency',
      flags: [],
      weight: 3,
      requires: ['natives'],
    },
    evaluate(signals) {
      if (signals.natives?.pristineRealmAvailable !== false) return null;
      return {
        confidence: 0.5,
        explanation:
          'Не получилось создать чистое окружение для сравнения встроенных функций. ' +
          'Часть проверок целостности не выполнялась — результат по ним не «чисто», ' +
          'а «неизвестно».',
        evidence: [{ label: 'Эталонный realm', expected: 'доступен', actual: 'недоступен' }],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'int.webrtc-disabled',
      title: 'WebRTC отключён',
      severity: 'low',
      category: 'privacy',
      flags: ['PRIVACY_TOOLING'],
      weight: 10,
      requires: ['network', 'navigator'],
    },
    evaluate(signals) {
      const network = signals.network;
      const parsed = signals.navigator?.parsed;
      if (!network?.webrtcDisabled || !parsed) return null;
      if (parsed.engine !== 'Blink' || parsed.isMobile) return null;

      return {
        confidence: 0.7,
        explanation:
          'В десктопном Chromium WebRTC включён по умолчанию и всегда возвращает хотя бы ' +
          'один локальный адрес. Здесь не вернулся ни один — обычно так делают расширения ' +
          'приватности и антидетект-браузеры, чтобы скрыть настоящий IP за прокси.',
        evidence: [{ label: 'Локальные ICE-кандидаты', expected: '≥ 1', actual: '0' }],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'int.webrtc-leak',
      title: 'WebRTC раскрывает локальные адреса',
      severity: 'info',
      category: 'privacy',
      flags: [],
      weight: 0,
      requires: ['network'],
    },
    evaluate(signals) {
      const addresses = signals.network?.localAddresses;
      if (!addresses || addresses.length === 0) return null;

      return {
        explanation:
          'WebRTC отдаёт настоящие адреса внутри вашей сети без маскировки через mDNS. ' +
          'Это не признак обмана, но заметная деталь: сайт узнаёт устройство ' +
          'внутри локальной сети даже за прокси. Полная проверка утечки требует ' +
          'сравнения с адресом соединения и появится вместе с серверной частью.',
        evidence: addresses.slice(0, 4).map((address) => ({ label: 'Локальный адрес', actual: address })),
      };
    },
  }),

  defineRule({
    meta: {
      id: 'int.timer-coarse',
      title: 'Точность таймеров искусственно снижена',
      severity: 'low',
      category: 'privacy',
      flags: ['PRIVACY_TOOLING'],
      weight: 6,
      requires: ['engine'],
    },
    evaluate(signals) {
      const resolution = signals.engine?.timerResolution;
      if (resolution === null || resolution === undefined) return null;
      // Штатное огрубление в Chrome — 0.1 мс, в Firefox с включённой защитой — 100 мс.
      if (resolution < 1) return null;

      return {
        confidence: 0.6,
        explanation:
          `Минимальный измеримый интервал времени — ${resolution} мс вместо долей миллисекунды. ` +
          'Так работает защита от атак по времени: её включают режимы усиленной приватности ' +
          'и Tor Browser.',
        evidence: [{ label: 'Разрешение таймера', expected: '< 1 мс', actual: `${resolution} мс` }],
      };
    },
  }),
];
